"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "@/lib/utils";

/**
 * Props for the MarkdownText component.
 *
 * @property content - The markdown string to render.
 * @property className - Optional additional CSS classes.
 * @property variant - Text size variant: "default" (text-sm) for forms,
 *   "compact" (text-xs) for canvas node descriptions,
 *   "large" (text-base) for hero / landing-page content.
 */
export interface MarkdownTextProps {
  content: string;
  className?: string;
  variant?: "default" | "compact" | "large";
}

/**
 * Renders a markdown string as styled HTML.
 *
 * Supports headings (h1–h6), bold, italic, links (opened in a new tab),
 * images, blockquotes, horizontal rules, ordered and unordered lists,
 * code blocks, and inline code. Raw HTML is stripped for safety.
 *
 * Three variants are available:
 * - `"default"` — `text-sm`, suitable for end-user forms.
 * - `"compact"` — `text-xs`, suitable for canvas node descriptions.
 * - `"large"`   — `text-base`, suitable for hero / landing-page content.
 */
export function MarkdownText(t0) {
  const $ = _c(10);
  const {
    content,
    className,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "default" : t1;
  const sizeClass = variant === "compact" ? "text-xs" : variant === "large" ? "text-base" : "text-sm";
  let t2;
  if ($[0] !== className || $[1] !== sizeClass) {
    t2 = cn(sizeClass, "text-foreground leading-relaxed", "[&>*+*]:mt-2", className);
    $[0] = className;
    $[1] = sizeClass;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [remarkGfm];
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      p: _temp,
      h1: _temp2,
      h2: _temp3,
      h3: _temp4,
      h4: _temp5,
      h5: _temp6,
      h6: _temp7,
      strong: _temp8,
      em: _temp9,
      a: _temp10,
      img: _temp11,
      blockquote: _temp12,
      hr: _temp13,
      ul: _temp14,
      ol: _temp15,
      li: _temp16,
      pre: _temp17,
      code: _temp18
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== content) {
    t5 = <ReactMarkdown remarkPlugins={t3} skipHtml={true} components={t4}>{content}</ReactMarkdown>;
    $[5] = content;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t2 || $[8] !== t5) {
    t6 = <div data-slot="markdown-text" className={t2}>{t5}</div>;
    $[7] = t2;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
}
function _temp18(t0) {
  const {
    children: children_14
  } = t0;
  return <code className="rounded bg-muted px-1 py-0.5 font-mono text-[0.9em]">{children_14}</code>;
}
function _temp17(t0) {
  const {
    children: children_13
  } = t0;
  return <pre className="rounded-lg bg-muted/50 border border-border/50 p-3 overflow-x-auto font-mono text-[0.9em]">{children_13}</pre>;
}
function _temp16(t0) {
  const {
    children: children_12
  } = t0;
  return <li>{children_12}</li>;
}
function _temp15(t0) {
  const {
    children: children_11
  } = t0;
  return <ol className="list-decimal pl-4 space-y-1">{children_11}</ol>;
}
function _temp14(t0) {
  const {
    children: children_10
  } = t0;
  return <ul className="list-disc pl-4 space-y-1">{children_10}</ul>;
}
function _temp13() {
  return <hr className="border-border my-4" />;
}
function _temp12(t0) {
  const {
    children: children_9
  } = t0;
  return <blockquote className="border-l-2 border-border pl-4 py-1 text-muted-foreground italic">{children_9}</blockquote>;
}
function _temp11(t0) {
  const {
    src,
    alt
  } = t0;
  const pathname = typeof src === "string" ? new URL(src, "https://placeholder.invalid").pathname : "";
  const isVideo = /\.(mp4|webm|ogg)$/i.test(pathname) || alt === "video";
  if (isVideo) {
    return <video src={src} controls={true} className="rounded-lg max-w-full my-2" />;
  }
  return <img src={src} alt={alt ?? ""} className="rounded-lg max-w-full h-auto my-2" loading="lazy" />;
}
function _temp10(t0) {
  const {
    href,
    children: children_8
  } = t0;
  return <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2 hover:text-primary/80">{children_8}</a>;
}
function _temp9(t0) {
  const {
    children: children_7
  } = t0;
  return <em className="italic">{children_7}</em>;
}
function _temp8(t0) {
  const {
    children: children_6
  } = t0;
  return <strong className="font-semibold">{children_6}</strong>;
}
function _temp7(t0) {
  const {
    children: children_5
  } = t0;
  return <h6 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mt-2 first:mt-0">{children_5}</h6>;
}
function _temp6(t0) {
  const {
    children: children_4
  } = t0;
  return <h5 className="text-sm font-semibold mt-2 first:mt-0">{children_4}</h5>;
}
function _temp5(t0) {
  const {
    children: children_3
  } = t0;
  return <h4 className="text-base font-semibold mt-3 first:mt-0">{children_3}</h4>;
}
function _temp4(t0) {
  const {
    children: children_2
  } = t0;
  return <h3 className="text-lg font-semibold tracking-tight mt-3 first:mt-0">{children_2}</h3>;
}
function _temp3(t0) {
  const {
    children: children_1
  } = t0;
  return <h2 className="text-xl font-semibold tracking-tight mt-4 first:mt-0">{children_1}</h2>;
}
function _temp2(t0) {
  const {
    children: children_0
  } = t0;
  return <h1 className="text-2xl font-semibold tracking-tight mt-4 first:mt-0">{children_0}</h1>;
}
function _temp(t0) {
  const {
    children
  } = t0;
  return <p>{children}</p>;
}